import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, pillClass, statusLabel, timeAgo, fmtDate } from "../lib/api";
import { Search, Phone } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Input } from "../components/ui/input";

export default function SearchPage() {
  const [q, setQ] = useState("");
  const [r, setR] = useState({ issues: [], meetings: [], notices: [], actions: [], flats: [], people: [] });

  useEffect(() => {
    if (!q) { setR({ issues: [], meetings: [], notices: [], actions: [], flats: [], people: [] }); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((res) => setR(res.data)), 300);
    return () => clearTimeout(t);
  }, [q]);

  const total = r.issues.length + r.meetings.length + r.notices.length + r.actions.length + r.flats.length + r.people.length;

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Memory</div>
      <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Search anything</h1>
      <div className="relative mt-5">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
          placeholder='Try "A-202", "Vikram", "9000", "lift", "water tank"…'
          className="h-14 pl-12 text-base" autoFocus />
      </div>
      {q && <div className="text-xs text-stone-500 mt-2">{total} result{total !== 1 ? "s" : ""}</div>}

      {r.flats.length > 0 && (
        <Block title="Flats">
          <div className="grid sm:grid-cols-2 gap-2">
            {r.flats.map((f) => (
              <div key={f.id} className="bg-white border border-stone-200 rounded-xl p-3">
                <div className="font-mono text-sm font-semibold">{f.label}</div>
                {f.block && <div className="text-xs text-stone-500">Block {f.block}</div>}
              </div>
            ))}
          </div>
        </Block>
      )}
      {r.people.length > 0 && (
        <Block title="People">
          {r.people.map((u) => (
            <div key={u.id} className="flex items-center justify-between bg-white border border-stone-200 rounded-xl p-3">
              <div>
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-stone-500 capitalize">{u.role}{u.phone && !u.phone_private ? ` · ${u.phone}` : ""}</div>
              </div>
              <div className="flex items-center gap-1">
                {u.phone && !u.phone_private && (
                  <>
                    <a href={`tel:${u.phone}`} className="text-stone-500 hover:text-[#C05640] p-2"><Phone size={16} /></a>
                    <a href={`https://wa.me/${u.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                      className="text-[#25D366] hover:opacity-80 p-2"><FaWhatsapp size={18} /></a>
                  </>
                )}
              </div>
            </div>
          ))}
        </Block>
      )}
      {r.issues.length > 0 && (
        <Block title="Issues">
          {r.issues.map((i) => (
            <Link key={i.id} to={`/issues/${i.id}`} className="block border border-stone-200 rounded-xl p-3 bg-white hover:border-stone-400">
              <div className="flex justify-between items-start gap-2">
                <div><span className="font-mono text-[10px] text-stone-500 mr-1.5">#{i.number}</span><span className="font-medium">{i.title}</span></div>
                <span className={pillClass(i.status)}>{statusLabel(i.status)}</span>
              </div>
              <div className="text-xs text-stone-500 mt-1">{i.category} · {timeAgo(i.created_at)}</div>
            </Link>
          ))}
        </Block>
      )}
      {r.meetings.length > 0 && (
        <Block title="Meetings">
          {r.meetings.map((m) => (
            <Link key={m.id} to={`/meetings/${m.id}`} className="block border border-stone-200 rounded-xl p-3 bg-white hover:border-stone-400">
              <div className="font-medium">{m.title}</div>
              <div className="text-xs text-stone-500">{fmtDate(m.date)}</div>
            </Link>
          ))}
        </Block>
      )}
      {r.notices.length > 0 && (
        <Block title="Notices">
          {r.notices.map((n) => (
            <div key={n.id} className="border border-stone-200 rounded-xl p-3 bg-white">
              <div className="font-medium">{n.title}</div>
              <div className="text-xs text-stone-600 line-clamp-2 mt-0.5">{n.body}</div>
            </div>
          ))}
        </Block>
      )}
      {r.actions.length > 0 && (
        <Block title="Actions">
          {r.actions.map((a) => (
            <div key={a.id} className="border border-stone-200 rounded-xl p-3 bg-white">
              <div className="font-medium">{a.title}</div>
              <div className="text-xs text-stone-500">{statusLabel(a.status)} · due {fmtDate(a.due_date)}</div>
            </div>
          ))}
        </Block>
      )}
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div className="mt-6">
      <h2 className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-2">{title}</h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
